/*
  Wishlist stored in this browser: per customer account when signed in, otherwise a guest list
  (merged into the account list the first time the customer signs in).
  Storage is isolated in `store` so it can later be swapped for an app-backed
  (cross-device) implementation without touching the UI code below.
*/
import { announce, formatMoney } from './theme.js';

const config = window.KidsWishlist || {};
const customerId = config.customerId || null;
const GUEST_KEY = 'kidsnest_wishlist_guest';
const storageKey = customerId ? `kidsnest_wishlist_${customerId}` : GUEST_KEY;
const EVENT = 'wishlist:change';

const store = {
  load(key = storageKey) {
    try {
      const list = JSON.parse(localStorage.getItem(key));
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  },
  save(list) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(list));
    } catch {
      /* storage full or blocked: keep the in-memory state only */
    }
  },
};

if (customerId) {
  const guest = store.load(GUEST_KEY);
  if (guest.length) {
    const mine = store.load();
    guest.forEach((item) => { if (!mine.some((m) => m.handle === item.handle)) mine.push(item); });
    store.save(mine);
    try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
  }
}

export const Wishlist = {
  get signedIn() {
    return Boolean(customerId);
  },
  get enabled() {
    return true;
  },
  items() {
    return store.load();
  },
  has(handle) {
    return this.items().some((item) => item.handle === handle);
  },
  toggle(handle, id) {
    const list = this.items();
    const index = list.findIndex((item) => item.handle === handle);
    const added = index === -1;
    if (added) list.unshift({ handle, id: Number(id) || null, addedAt: Date.now() });
    else list.splice(index, 1);
    store.save(list);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { handle, added, count: list.length } }));
    return added;
  },
  remove(handle) {
    if (this.has(handle)) this.toggle(handle);
  },
};

function syncButtons(root = document) {
  root.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    const on = Wishlist.has(btn.dataset.handle);
    btn.setAttribute('aria-pressed', String(on));
    btn.classList.toggle('is-active', on);
    const label = btn.querySelector('[data-wishlist-label]');
    if (label) label.textContent = on ? label.dataset.remove : label.dataset.add;
  });
  const count = Wishlist.items().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
  });
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const added = Wishlist.toggle(btn.dataset.handle, btn.dataset.id);
  btn.classList.remove('is-pop');
  void btn.offsetWidth;
  btn.classList.add('is-pop');
  announce(added ? 'Saved to your wishlist ♡' : 'Removed from your wishlist', { toast: true });
});

document.addEventListener(EVENT, () => syncButtons());
// another tab changed the list
window.addEventListener('storage', (e) => {
  if (e.key === storageKey) syncButtons();
});
// cards rendered later (quick view, recommendations, carousels)
new MutationObserver((records) => {
  if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-wishlist-toggle]') || n.querySelector?.('[data-wishlist-toggle]'))))) syncButtons();
}).observe(document.documentElement, { childList: true, subtree: true });
syncButtons();

/* ---------- Wishlist page ---------- */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.render();
    this.onChange = () => this.render();
    document.addEventListener(EVENT, this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onChange);
  }

  show(state) {
    this.querySelectorAll('[data-state]').forEach((el) => {
      el.hidden = el.dataset.state !== state;
    });
  }

  async render() {
    const items = Wishlist.items();
    if (!items.length) return this.show('empty');
    this.show('list');
    const grid = this.querySelector('[data-wishlist-items]');
    const products = await Promise.all(
      items.map((item) =>
        fetch(`${window.Shopify?.routes?.root || '/'}products/${item.handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    const cards = products.filter(Boolean).map((p) => {
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
      const compare = !window.Diecela?.hideCompare && p.compare_at_price > p.price ? `<s>${formatMoney(p.compare_at_price)}</s>` : '';
      return `
        <li class="wishlist-card">
          <a class="wishlist-card__media" href="${p.url}">${img ? `<img src="${img}" alt="" loading="lazy">` : ''}</a>
          <button type="button" class="wishlist-btn wishlist-btn--icon is-active wishlist-card__remove" data-wishlist-toggle data-handle="${p.handle}" data-id="${p.id}" aria-pressed="true" aria-label="Remove ${p.title.replace(/"/g, '&quot;')} from your wishlist">
            <svg class="icon icon--heart" aria-hidden="true" focusable="false"><use href="#icon-heart"/></svg>
          </button>
          <div class="wishlist-card__info">
            <a class="wishlist-card__title" href="${p.url}">${p.title}</a>
            <span class="wishlist-card__price"><strong>${formatMoney(p.price)}</strong> ${compare}</span>
            <a class="button button--full wishlist-card__cta" href="${p.url}">${p.available ? 'View details' : 'Sold out — view'}</a>
          </div>
        </li>`;
    });
    // products that no longer exist are dropped quietly
    if (cards.length !== items.length) {
      products.forEach((p, i) => { if (!p) Wishlist.remove(items[i].handle); });
    }
    grid.innerHTML = cards.join('');
    const counter = this.querySelector('[data-wishlist-total]');
    if (counter) counter.textContent = String(cards.length);
    syncButtons(this);
  }
}
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
